import React, { Fragment, useCallback, useEffect, useState } from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetFlatList,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { Pressable, Text, useWindowDimensions, View } from "react-native";
import { useSheet } from "../shared/sheet-provider";
import CloseIcon from "@/assets/svgs/close-icon";
import { remapProps } from "react-native-css-interop";
import { SheetIds } from "../utils/sheet-ids";
import useResponsiveWidth from "@/elements/hooks/use-responsive-width";
import { RadioInput } from "../ui/radio-input";
import { Button } from "../ui/button";
import { useIsFocused } from "@react-navigation/native";

type Option = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

type Options = Option[];

export type TimeSlotsSelectSheetPayload = {
  options: Option[];
  preSelectedOption: string | number;
};

export type TimeSlotsSelectSheetResult = {
  selectedOption: string | number;
};

remapProps(BottomSheetView, {
  className: "style",
});

remapProps(BottomSheet, {
  className: "containerStyle",
});

function TimeSlotsSelectSheet() {
  const [shouldRender, setShouldRender] = useState(false);
  const { register, close, dismiss, sheet } = useSheet<
    TimeSlotsSelectSheetPayload,
    TimeSlotsSelectSheetResult
  >(SheetIds.TIME_SLOTS_SELECT);
  const { payload } = sheet ?? {};
  const options: Options = payload?.options || [];
  const preSelectedOption: string = payload?.preSelectedOption || "";
  const [selectedOption, setSelectedOption] = useState<string | number>(
    preSelectedOption
  );
  const responsiveWidth = useResponsiveWidth();
  const { width } = useWindowDimensions();
  const marginLeft = (width - responsiveWidth) / 2;
  const isFocused = useIsFocused();

  useEffect(() => {
    if (isFocused) {
      setShouldRender(true);
    } else {
      setShouldRender(false);
    }
  }, [isFocused]);

  const setBottomSheetRef = useCallback(
    (ref: BottomSheet | null) => {
      if (ref) {
        const refObject = { current: ref };
        register(refObject);
      }
    },
    [register]
  );

  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === -1) {
        dismiss();
      }
      if (index === 0) {
        setSelectedOption(preSelectedOption);
      }
    },
    [dismiss, preSelectedOption]
  );


  const onClose = useCallback(() => {
    close({
      selectedOption: preSelectedOption,
    });
  }, [close, preSelectedOption]);

  const onDone = useCallback(() => {
    close({
      selectedOption,
    });
  }, [close, selectedOption]);

  const onSelect = useCallback(
    (value: string | number) => {
      setSelectedOption(value);
    },
    []
  );

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
        onPress={onClose}
      />
    ),
    [onClose]
  );

  const renderItem = useCallback(
    ({ item }: { item: Option }) => {
      const onPress = () => {
        setSelectedOption(item.value);
        onSelect(item.value);
      };

      return (
        <View className="mx-5">
          <RadioInput onPress={onPress} disabled={item.disabled}>
            <RadioInput.Label>{item.label}</RadioInput.Label>
            <RadioInput.Icon checked={selectedOption === item.value} />
          </RadioInput>
        </View>
      );
    },
    [onSelect, selectedOption]
  );

  return shouldRender ? (
    <BottomSheet
      ref={setBottomSheetRef}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      index={-1}
      handleComponent={() => <Fragment />}
      onChange={handleSheetChanges}
      backgroundStyle={{
        backgroundColor: "#FCF9F5",
      }}
      style={{ width: responsiveWidth, marginLeft: marginLeft }}
    >
      <BottomSheetFlatList
        data={options}
        renderItem={renderItem}
        ItemSeparatorComponent={() => <View className="h-2" />}
        ListHeaderComponent={() => (
          <View className="bg-teachLoop-creamBase rounded-t-[20]">
            <View className="h-6" />
            <View className="mx-5 flex-row">
              <Text className="font-geologica-medium text-black text-xl leading-[1.5] flex-1">
                Pick a time slot
              </Text>
              <Pressable onPress={onClose} className="active:opacity-80">
                <CloseIcon
                  className="size-6"
                  pathClassName="stroke-teachLoop-textPrimary"
                />
              </Pressable>
            </View>
            <View className="h-6" />
          </View>
        )}
        stickyHeaderIndices={[0]}
        ListFooterComponent={() =>
          options.length <= 5 ? (
            <View className="m-5">
              <Button
                className="w-full"
                size="md"
                intent="primary"
                onPress={onDone}
              >
                <Button.Text>Done</Button.Text>
              </Button>
            </View>
          ) : (
            <View className="h-2" />
          )
        }
      />
      {options.length > 5 && (
        <View className="m-5">
          <Button
            className="w-full"
            size="sm"
            intent="primary"
            onPress={onDone}
          >
            <Button.Text>Done</Button.Text>
          </Button>
        </View>
      )}
    </BottomSheet>
  ) : null;
}

export default TimeSlotsSelectSheet;
